// Axis-locked gesture arbitration for the paged-flip engine.
//
// Pure and unit-testable across the full angle range. This is where T1's
// regression test lives (docs/architecture/29-templates-themes-and-the-mnrq-renderer.md §5.2 / R29.2).
//
// The engine must not claim a gesture until its dominant axis is known. On a
// vertical-dominant gesture the browser keeps the scroll; on a horizontal-dominant
// gesture the flip engine claims it.

export type GestureVerdict = 'undecided' | 'horizontal' | 'vertical';

const DOMINANCE_RATIO = 1.5;

/**
 * Decide the dominant axis of a drag given deltas from the gesture origin.
 *
 * @param dx        Horizontal delta (signed is fine; magnitude is used).
 * @param dy        Vertical delta (signed is fine; magnitude is used).
 * @param threshold Travel (in px) below which a gesture is still ambiguous.
 * @returns 'vertical' once the drag is clearly scrolling, 'horizontal' once it is
 *          clearly a page turn, and 'undecided' before that or on a borderline angle.
 */
export function classify(dx: number, dy: number, threshold: number): GestureVerdict {
  const adx = Math.abs(dx);
  const ady = Math.abs(dy);

  if (adx < threshold && ady < threshold) return 'undecided';

  if (ady > threshold && ady > adx * DOMINANCE_RATIO) return 'vertical';
  if (adx > threshold && adx > ady * DOMINANCE_RATIO) return 'horizontal';

  return 'undecided';
}
